const SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const SCRIPT_ID = 'sayable-openai-ad-pixel';

// Pixel IDs are public browser identifiers, not API credentials.
const PIXELS = {
  dev: { pixelId: 'DqYpYr5bu9wXCfY8RSoGVC', debug: true },
  production: { pixelId: 'PjzUzs8sbj6db5wJGEHY4F', debug: false },
};

let initialized = false;

export function initializeOpenAIAdPixel() {
  if (typeof window === 'undefined' || typeof document === 'undefined'
      || process.env.NODE_ENV === 'test' || initialized) return;

  // The development server always uses the dev pixel, even with copied prod settings.
  const environment = process.env.NODE_ENV === 'development' ? 'dev' : process.env.REACT_APP_ENV;
  const config = environment === 'dev' ? PIXELS.dev
    : environment === 'production' ? PIXELS.production : null;
  if (!config || document.getElementById(SCRIPT_ID)) return;

  try {
    initializePixel(config);
    initialized = true;
  } catch {
    // An unavailable or blocked advertising integration must not prevent the app from starting.
  }
}

function initializePixel(config) {
  if (!window.oaiq) {
    const queue = function () { queue.q.push(arguments); };
    queue.q = [];
    window.oaiq = queue;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SDK_URL;
    document.head.appendChild(script);
  }

  window.oaiq('init', { ...config });
}

const SIGNUP_EVENT_KEY = 'sayable-openai-signup-event';

// Activation redirects to onboarding with a full page load. Carry only an anonymous
// event ID across that navigation, never the email address or verification token.
export function rememberCompletedSignup() {
  try {
    window.sessionStorage.setItem(SIGNUP_EVENT_KEY, JSON.stringify({
      id: window.crypto.randomUUID(),
      createdAt: Date.now(),
    }));
  } catch {
    // Tracking must not interfere with activation if browser storage is unavailable.
  }
}

export function measureCompletedSignup() {
  if (typeof window === 'undefined' || !initialized
      || window.location.pathname !== '/onboarding') return;

  try {
    const stored = window.sessionStorage.getItem(SIGNUP_EVENT_KEY);
    if (!stored) return;
    // Consume once, including across reloads and React Strict Mode remounts.
    window.sessionStorage.removeItem(SIGNUP_EVENT_KEY);
    const event = JSON.parse(stored);
    const age = Date.now() - event.createdAt;
    if (typeof event.id !== 'string' || !event.id
        || !Number.isFinite(age) || age < 0 || age > 10 * 60 * 1000) return;
    window.oaiq('measure', 'registration_completed', { type: 'customer_action' }, {
      event_id: event.id,
    });
  } catch {
    // A blocked pixel must not prevent onboarding from starting.
  }
}

const measuredOnboardingPlans = new Set();

export function measureOnboardingCompletion(previous, current) {
  if (typeof window === 'undefined' || !initialized
      || !previous?.id || previous.id !== current?.id
      || !previous.status || previous.status === 'completed'
      || current.status !== 'completed' || measuredOnboardingPlans.has(current.id)) return;

  try {
    // Only an observed transition counts. Opening an already completed plan does not.
    // Keep the plan ID local; send only an anonymous event ID to the pixel.
    const eventId = window.crypto.randomUUID();
    measuredOnboardingPlans.add(current.id);
    window.oaiq('measure', 'custom', { type: 'custom' }, {
      custom_event_name: 'onboarding_completed',
      event_id: eventId,
    });
  } catch {
    // Tracking must not interfere with the completed setup experience.
  }
}

export function measureLandingPageView() {
  if (typeof window === 'undefined' || !initialized
      || window.location.pathname !== '/') return;
  try {
    window.oaiq('measure', 'page_viewed', { type: 'contents' });
  } catch {
    // A blocked pixel must not affect the landing page.
  }
}
